<template>
	<header v-if="!isAuth && !isFullPageContent && !isErrorPage" v-cloak id="header" class="header minified-sidebar" :class="[whichSection]">
		<HeaderPartial />
	</header>

	<aside v-if="!isFullPageContent && !isErrorPage" v-cloak id="notification" class="notification">
		<NotificationPartial />
	</aside>

	<aside v-if="!isAuth && !isFullPageContent && !isErrorPage" v-cloak id="sidebar" class="sidebar minified-sidebar" :class="[whichSection]">
		<SidebarPartial />
	</aside>

	<main
		v-cloak
		id="content"
		class="content minified-sidebar"
		:class="{ 'auth-page': isAuth, 'not-found': isNotFound, 'action-bar-active': isActionBarActive, 'full-page-content': isFullPageContent }"
	>
		<div v-if="!isFullPageContent" class="breadcrumbs">
			<RouterLink v-if="showBackButton" :to="backButtonPath" class="back-link">
				<i class="header-icon fas fa-arrow-left" />
				<span v-text="backButtonText" />
			</RouterLink>
		</div>

		<RouterView v-slot="{ Component }">
			<component :is="Component" :key="getRoutePath" />
		</RouterView>
	</main>
</template>

<script setup lang="ts">
	import { onMounted, watch, computed, inject, ref } from 'vue';
	import type { HttpHelper } from './helper/http';
	import { type RouteLocationNormalized, type NavigationGuardNext, useRouter, useRoute } from 'vue-router';
	import { useStore } from 'vuex';
	import HeaderPartial from './partials/Header.vue';
	import SidebarPartial from './partials/Sidebar.vue';
	import NotificationPartial from './partials/Notification.vue';
	import { REGEX_ROOT_PATH } from './constants/regex';

	const store = useStore();
	const router = useRouter();
	const route = useRoute();
	const http = inject<HttpHelper>('http')!;
	const showBackButton = ref(false);
	const backButtonText = ref('');
	const backButtonPath = ref('');

	const whichSection = computed(() => {
		if (REGEX_ROOT_PATH.test(route.path)) {
			return 'dashboard';
		} else if (route.path.startsWith('/create/')) {
			return 'create';
		} else if (route.path.startsWith('/rpa/')) {
			return 'rpa';
		} else if (route.path.startsWith('/ai/')) {
			return 'ai';
		} else if (route.path.startsWith('/converse-cx/')) {
			return 'converse';
		} else if (route.path.startsWith('/spark/')) {
			return 'spark';
		} else if (route.path.startsWith('/idp/')) {
			return 'idp';
		}
		return 'controller';
	});

	const getRoutePath = computed(() => {
		const parts: Array<string> = route.path.split('/').filter((part: string) => part !== '' ? part : undefined);
		if (parts[0] === 'ai' && parts[1] === 'agent') {
			if (parts.length > 3) {
				return `agent_${parts[2]}`;
			}
		} else if (parts[0] === 'create') {
			if (parts.length > 4) {
				return `${parts[2]}_${parts[3]}`;
			} else if (parts.length > 3) {
				return `app-${parts[2]}`;
			} else {
				return parts.join('_');
			}
		} else if (parts[0] === 'settings') {
			return 'settings';
		} else {
			return parts.join('_');
		}
		return '';
	});

	const isNotFound = computed(() => {
		return route.name === 'NotFound';
	});

	const isAuth = computed(() => {
		if (route.path.includes('/auth/')) return true;
		return false;
	});

	const isFullPageContent = computed(() => {
		if (route.path.includes('/confusion-matrix/')) return true;
		return false;
	});

	const isErrorPage = computed(() => {
		if (route.path.includes('/page404')) return true;
		if (route.path.toLowerCase().includes('/forbidden')) return true;
		return false;
	});

	const isActionBarActive = computed(() => {
		const routeParts = route.path.split('/');
		if (routeParts[1].trim() === 'create' && routeParts[2].trim() === 'application') {
			if (routeParts[5].trim() === 'overview' || routeParts[5].trim() === 'users' || routeParts[5].trim() === 'resources') {
				return true;
			}
		}
		return false;
	});

	const isLoggedIn = computed(() => {
		return store.state.account.isLoggedIn;
	});

	const processBackButton = (to: RouteLocationNormalized) => {
		const pathParts: Array<string> = to.path.split('/');
		if (to.path.includes('/create/application') && pathParts.length > 5) {
			showBackButton.value = true;
			backButtonPath.value = `/create/applications/${pathParts[3]}/environments`;
			backButtonText.value = 'To App';
		} else if (to.path.includes('/create/application') && pathParts.length > 4) {
			showBackButton.value = true;
			backButtonPath.value = '/create/applications';
			backButtonText.value = 'To Apps List';
		} else if (to.path.includes('/ai/model') && pathParts.length >= 4) {
			showBackButton.value = true;
			backButtonPath.value = '/ai/models';
			backButtonText.value = 'To AI Models';
		} else if (to.path.includes('/ai/knowledge-management/local/collection/') && pathParts.length >= 5) {
			showBackButton.value = true;
			backButtonPath.value = '/ai/knowledge-management/local/collections';
			backButtonText.value = 'To Knowledge Management';
		} else if (to.path.includes('/ai/knowledge-management/dynamic/group/') && pathParts.length >= 5) {
			showBackButton.value = true;
			backButtonPath.value = '/ai/knowledge-management/dynamic/groups';
			backButtonText.value = 'To Groups';
		} else {
			showBackButton.value = false;
		}
	};

	const hideLoader = () => {
		setTimeout(() => {
			const loader = document.getElementById('loader');
			if (loader) loader.style.display = 'none';
		}, 250);
	};

	const loadConfiguration = async () => {
		if (!isLoggedIn.value) return;
		const response = await http.get('/api/configuration', { autoRefresh: 'auto' });
		if (response.status === 200) {
			const result = await response.json();
			store.dispatch('setConfiguration', result);
		}
	};

	const getErrorMessage = async () => {
		const response = await http.post('/api/error_message', { uuid: route.query.e });
		if (response.status === 200) {
			const result = await response.json(); ;

			return result?.error_message;
		}

		return '';
	};

	onMounted(async () => {
		router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
			processBackButton(to);
			return next();
		});

		await loadConfiguration();

		setInterval(() => {
			loadConfiguration();
		}, 60_000);

		hideLoader();

		watch(() => route.query, async () => {
			if (route.query.e) {
				const message = await getErrorMessage();

				if (message.length <= 0) return;

				store.dispatch('notification/add', {
					type: 'error',
					message,
				});
			}
		});
	});
</script>

<style lang="scss">
	@use './assets/styles/base';
	@use './assets/styles/base/_core';
	@use './assets/styles/base/_reset';
	@use './assets/styles/base/_normalise';
	@use './assets/styles/partials/layout';
	@use './assets/styles/modules';

	.breadcrumbs {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1.5rem;
	}
</style>
